import { useEffect, useState } from 'react';

import { getProjectDetails } from '@/api/project-details/api';
import type { StatusUpdate } from '@/api/project-details/mocks/projectHome.types';
import type { PreviousWeeklyUpdateRecord } from '@/hooks/useWeeklyUpdate';
import { createLogger } from '@/utils/logging';

const logger = createLogger('hooks', 'useMostRecentWeeklyUpdate');

interface UseMostRecentWeeklyUpdateReturn {
  data: PreviousWeeklyUpdateRecord | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetch the most recent available weekly update for a project.
 * Filters to find updates before the current week and returns the latest one.
 * Used in create mode to show previous week as reference (or any available previous week).
 */
export function useMostRecentWeeklyUpdate(
  accessToken: string | null | undefined,
  clientId: string,
  projectId: string,
  currentWeek: string,
): UseMostRecentWeeklyUpdateReturn {
  const [data, setData] = useState<PreviousWeeklyUpdateRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!accessToken || !currentWeek) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchMostRecent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all project details including all updates
        const details = await getProjectDetails(
          accessToken,
          clientId,
          projectId,
          'metadata,team,health,trends,audit,updates',
        );

        if (cancelled) {
          return;
        }

        // Find the most recent update that is before the current week
        const statusUpdates = (details.statusUpdates || []) as StatusUpdate[];
        const previousUpdates = statusUpdates.filter((update) => update.weekOf < currentWeek);

        // Sort by weekOf descending to get the most recent
        previousUpdates.sort((a, b) => b.weekOf.localeCompare(a.weekOf));

        setData(
          previousUpdates.length > 0
            ? (previousUpdates[0] as unknown as PreviousWeeklyUpdateRecord)
            : null,
        );
      } catch (err) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error(String(err));
          logger.error(
            `Failed to fetch most recent weekly update for project ${projectId}:`,
            error,
          );
          setError(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchMostRecent();

    return () => {
      cancelled = true;
    };
  }, [accessToken, clientId, projectId, currentWeek]);

  return { data, loading, error };
}
